import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { hostService } from "@/lib/services";
import { formatUsh } from "@/lib/format";
import type { Listing } from "@/types/api";
import {
  PropertyVerificationBadge,
  PropertyVerificationNote,
} from "@/components/property-verification-mark";

export const Route = createFileRoute("/_authenticated/host/listings/")({
  component: HostListings,
});

function HostListings() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["host-listings"],
    queryFn: () => hostService.listings(),
  });
  const items = (data && (data as { data?: Listing[] }).data) ?? [];

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-ink">My listings</h1>
          <p className="mt-1 text-sm text-ink-muted">Bookable units across all your properties.</p>
        </div>
        <Link
          to="/host/listings/new"
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> New listing
        </Link>
      </div>

      {isLoading && (
        <div className="mt-6 space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-2xl border border-border brand-skeleton"
            />
          ))}
        </div>
      )}

      {isError && (
        <div className="mt-6 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {(error as Error).message}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="mt-10 rounded-3xl border border-border bg-card p-10 text-center">
          <p className="text-base font-bold text-ink">No listings yet</p>
          <p className="mt-1 text-sm text-ink-muted">
            Add one under any of your properties — it does not have to be verified first.
          </p>
        </div>
      )}

      <ul className="mt-6 space-y-3">
        {items.map((l) => (
          <li key={l.id}>
            <Link
              to="/listings/$listingId"
              params={{ listingId: l.id }}
              className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-card)]"
            >
              <div className="h-16 w-20 shrink-0 overflow-hidden rounded-xl bg-muted">
                {l.images?.[0] && (
                  <img src={l.images[0]} alt="" className="h-full w-full object-cover" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-extrabold text-ink">{l.title}</p>
                <p className="flex items-center gap-2 text-xs text-ink-muted">
                  <span className="truncate">{l.property?.name ?? "—"}</span>
                  {/*
                    The same mark the guest sees, on the host's own row. A host
                    should never learn how their listing is presented by going
                    and looking at it as a stranger.
                  */}
                  {l.property && !l.property.verified && (
                    <PropertyVerificationBadge verified={false} className="shrink-0" />
                  )}
                </p>
                <p className="mt-1 text-[11px] uppercase tracking-wider text-ink-subtle">
                  {l.bedrooms} bd · {l.bathrooms} ba · max {l.max_guests}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-extrabold text-ink">{formatUsh(l.price_per_night)}</p>
                <p className="text-[11px] uppercase tracking-wider text-ink-subtle">/ night</p>
                {!l.is_active && (
                  <span className="mt-1 inline-block rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase text-ink-muted">
                    Inactive
                  </span>
                )}
              </div>
            </Link>

            {l.property && !l.property.verified && (
              <div className="mt-2">
                <PropertyVerificationNote
                  verified={false}
                  audience="host"
                  propertyName={l.property.name}
                />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
